import { ExpandMore, KeyboardDoubleArrowLeft } from '@mui/icons-material';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { SiteHeaderNavLink } from './site-header';
import { Logo } from '../../assets/logo';
import { useMuiSwitchableTheme } from '../../theme';
import { LogoText } from '../../assets/logo-text';
import { EnvironmentAlert } from './app-bar';

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
})) as any;

const NavLink = ({
  href,
  label,
  icon,
  dropDownItems,
  handleDrawerClose,
  collapsed,
  onClick,
  onDropdownToggle,
  isOpen,
  onToggleOpen,
  navIndex,
}: SiteHeaderNavLink & {
  handleDrawerClose: () => void;
  collapsed: boolean;
  onDropdownToggle: () => void;
  isOpen: boolean;
  onToggleOpen: (index: number) => void;
  navIndex: number;
}) => {
  const handleClick = () => {
    onClick?.();
    if (dropDownItems?.length) {
      if (collapsed) {
        // If collapsed and has dropdown items, expand the drawer first
        onDropdownToggle();
      }
      onToggleOpen(navIndex);
    } else {
      handleDrawerClose();
    }
  };

  if (!dropDownItems?.length) {
    return (
      <ListItem disablePadding>
        <ListItemButton
          onClick={handleClick}
          component={href ? Link : 'div'}
          to={href}
        >
          <ListItemIcon
            sx={{ color: 'inherit', minWidth: collapsed ? 'auto' : 56 }}
          >
            {icon}
          </ListItemIcon>
          <ListItemText
            primary={label}
            sx={{
              opacity: collapsed ? 0 : 1,
              transition: 'opacity 0.3s ease',
              textWrap: 'nowrap',
            }}
          />
        </ListItemButton>
      </ListItem>
    );
  }

  return (
    <>
      <ListItem
        sx={{ my: isOpen ? 0.5 : 0, transition: 'margin 0.3s ease' }}
        disablePadding
        secondaryAction={
          !collapsed ? (
            <ExpandMore
              sx={{
                transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s ease',
                color: 'inherit',
              }}
            />
          ) : null
        }
      >
        <ListItemButton onClick={handleClick}>
          <ListItemIcon
            sx={{ color: 'inherit', minWidth: collapsed ? 'auto' : 56 }}
          >
            {icon}
          </ListItemIcon>
          <ListItemText
            primary={label}
            sx={{
              opacity: collapsed ? 0 : 1,
              transition: 'opacity 0.3s ease',
              textWrap: 'nowrap',
            }}
          />
        </ListItemButton>
      </ListItem>
      <List
        sx={{
          m: 0,
          p: 0,
          maxHeight: isOpen && !collapsed ? 1 : 0,
          overflow: 'hidden',
          transition: 'max-height 0.3s ease',
        }}
      >
        {dropDownItems.map((item, idx) => (
          <ListItem key={idx} disablePadding>
            <ListItemButton
              onClick={() => {
                item.onClick?.();
                handleDrawerClose();
              }}
              {...(item.href && {
                component: Link,
                to: item.href,
                target: item.external ? '_blank' : '_self',
              })}
            >
              <ListItemIcon />
              <ListItemText
                primary={item.label}
                sx={{
                  opacity: collapsed ? 0 : 1,
                  transition: 'opacity 0.3s ease',
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </>
  );
};

export const LeftNav = ({
  items = [],
  handleDrawerClose = () => {},
  authComponent = null,
  baseColor = 'success',
  env = '',
}: {
  items?: SiteHeaderNavLink[];
  drawerWidth?: number;
  handleDrawerClose?: () => void;
  authComponent?: React.ReactNode;
  baseColor?:
    | 'success'
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'warning'
    | 'error';
  env?: string;
}) => {
  const {
    navCollapsed: collapsed,
    setNavCollapsed: setCollapsed,
    navMode,
    navWidth,
    themeMode,
  } = useMuiSwitchableTheme();

  const [openDropdownIndex, setOpenDropdownIndex] = useState<number | null>(
    null,
  );

  const persistent = navMode === 'left';

  const handleToggleCollapse = () => {
    setCollapsed(!collapsed);
  };

  const handleDropdownToggle = () => {
    if (collapsed) {
      setCollapsed(false);
    }
  };

  const handleToggleOpen = (index: number) => {
    // If clicking the same dropdown that's already open, close it
    // Otherwise, open the clicked dropdown (which automatically closes others)
    setOpenDropdownIndex(openDropdownIndex === index ? null : index);
  };

  // Close all dropdowns when collapsed
  useEffect(() => {
    if (collapsed) {
      setOpenDropdownIndex(null);
    }
  }, [collapsed]);

  return (
    <Drawer
      sx={{
        width: navWidth,
        flexShrink: 0,
        display: persistent ? 'flex' : { xs: 'flex', md: 'none' },
        '& .MuiDrawer-paper': {
          width: navWidth,
          boxSizing: 'border-box',
          backgroundColor: `${baseColor}.${
            themeMode === 'light' ? 'main' : 'dark'
          }`,
          color: `${baseColor}.contrastText`,
          transition: 'width 0.3s ease',
          overflowX: 'hidden',
          height: '100dvh',
          backgroundImage: 'none',
        },
        '@media print': { display: 'none' },
      }}
      variant={persistent ? 'permanent' : 'temporary'}
      anchor="left"
      open={persistent ? true : !collapsed}
      onClose={handleDrawerClose}
    >
      <DrawerHeader
        sx={{ justifyContent: 'center', flexDirection: 'column', my: 2 }}
      >
        {collapsed ? (
          <Logo
            sx={{ width: 32, height: 32, transition: 'opacity 0.3s ease' }}
          />
        ) : (
          <LogoText
            sx={{
              height: 32,
              maxWidth: 120,
              flexGrow: 1,
              mr: 1,
              color: `${baseColor}.contrastText`,
            }}
          />
        )}
        <EnvironmentAlert env={env} collapsed={collapsed} />
      </DrawerHeader>
      <List sx={{ flex: 1, overflowY: 'scroll', scrollbarWidth: 'none' }}>
        {items.map((item, idx) => (
          <NavLink
            key={idx}
            {...item}
            handleDrawerClose={handleDrawerClose}
            collapsed={collapsed}
            onDropdownToggle={handleDropdownToggle}
            isOpen={openDropdownIndex === idx}
            onToggleOpen={handleToggleOpen}
            navIndex={idx}
          />
        ))}
      </List>
      {authComponent && (
        <Box sx={{ p: collapsed ? 1 : 2, transition: 'padding 0.3s ease' }}>
          <Box
            sx={{
              maxWidth: navWidth,
              overflow: 'hidden',
              transition: 'max-width 0.3s ease',
            }}
          >
            {authComponent}
          </Box>
        </Box>
      )}
      <Box sx={{ p: 1, display: 'flex', justifyContent: 'center' }}>
        <ListItem disablePadding>
          <ListItemButton onClick={handleToggleCollapse}>
            <ListItemIcon
              sx={{ color: 'inherit', minWidth: collapsed ? 'auto' : 56 }}
            >
              <KeyboardDoubleArrowLeft
                sx={{
                  transform: `rotate(${collapsed ? 180 : 0}deg)`,
                  transition: 'transform 0.3s ease',
                }}
              />
            </ListItemIcon>
            <ListItemText
              primary="Collapse"
              sx={{
                opacity: collapsed ? 0 : 1,
                transition: 'opacity 0.3s ease',
              }}
            />
          </ListItemButton>
        </ListItem>
      </Box>
    </Drawer>
  );
};
